import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PasswordField } from "@/components/ui/PasswordField";
import { TID } from "@/constants/testIds";

async function nukeSWAndReload() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) await r.unregister();
    }
    if (window.caches) {
      const keys = await caches.keys();
      for (const k of keys) await caches.delete(k);
    }
  } catch {}
  window.location.reload();
}

export default function Login() {
  const { login, user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(email, password); toast.success(t("welcomeBack")); nav("/"); }
    catch (err) {
      // If the request never got a proper response (SW/CDN blocking) — self-heal.
      if (!err?.response) { toast.error(t("invalidCreds") + " — resetting cache…"); setTimeout(nukeSWAndReload, 800); return; }
      toast.error(err?.response?.data?.detail || t("invalidCreds"));
    }
    finally { setBusy(false); }
  };
  const input = "w-full px-4 py-3 rounded-2xl neu-inset focus:outline-none focus:ring-2 focus:ring-[#1e3a8a55]";
  return (
    <div className="neu-page min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden p-12 items-center justify-center">
        <div className="neu p-12 max-w-md w-full">
          <div className="w-16 h-16 rounded-2xl neu-fill-blue grid place-items-center mb-6 shadow-xl shadow-blue-900/30">
            <Wallet className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-5xl font-extrabold neu-accent-blue leading-tight mb-3">{t("appName")}</h1>
          <p className="text-lg text-slate-500">{t("tagline")}</p>
          <div className="mt-6 inline-flex items-center gap-2 neu-inset rounded-full px-4 py-2 text-sm neu-accent-blue font-semibold">
            <Sparkles className="w-4 h-4 neu-accent-yellow" /> Voice · Photo · Manual
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md neu p-8 space-y-5">
          <h2 className="font-display text-3xl font-bold neu-accent-blue">{t("login")}</h2>
          <label className="block space-y-2"><span className="text-sm font-medium">{t("email")}</span>
            <input data-testid={TID.loginEmail} required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
          </label>
          <label className="block space-y-2"><span className="text-sm font-medium">{t("password")}</span>
            <PasswordField testId={TID.loginPassword} value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button data-testid={TID.loginSubmit} disabled={busy} type="submit"
            className="w-full py-3 rounded-2xl neu-fill-blue text-white font-semibold shadow-lg shadow-blue-900/30 hover:shadow-xl hover:shadow-blue-900/40 active:scale-[0.98] transition-all disabled:opacity-60">
            {busy ? "…" : t("login")}
          </button>
          <p className="text-sm text-slate-500 text-center">{t("noAccount")} <Link className="neu-accent-blue font-semibold hover:underline" to="/signup">{t("signup")}</Link></p>
          <button type="button" onClick={nukeSWAndReload} className="w-full text-xs text-slate-400 hover:text-slate-600 py-1">
            {t("cacheReset")}
          </button>
        </form>
      </div>
    </div>
  );
}
